/**
 * Broadcast Client Service
 * HTTP/2 + SSE 기반 실시간 브로드캐스팅 클라이언트
 */

import { BroadcastMessage } from './protocol';

export interface BroadcastClientConfig {
  serverUrl: string;
  deviceId: string;
  autoReconnect?: boolean;
}

export interface PublishResult {
  ok: boolean;
  subscribers: number;
  messageCount: number;
}

export class BroadcastClient {
  private serverUrl: string;
  private deviceId: string;
  private autoReconnect: boolean;
  private eventSource: EventSource | null = null;
  private isConnected: boolean = false;
  private reconnectAttempts: number = 0;
  private maxReconnectAttempts: number = 5;
  private reconnectDelay: number = 1000;

  constructor(config: BroadcastClientConfig) {
    this.serverUrl = config.serverUrl;
    this.deviceId = config.deviceId;
    this.autoReconnect = config.autoReconnect ?? true;
  }

  /**
   * Publisher: 데이터 발행
   */
  async publish(payload: BroadcastMessage['payload']): Promise<PublishResult> {
    try {
      const response = await fetch(`${this.serverUrl}/publish`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          deviceId: this.deviceId,
          timestamp: Date.now(),
          payload
        } as BroadcastMessage)
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Failed to publish:', error);
      throw error;
    }
  }

  /**
   * Subscriber: SSE로 데이터 구독
   */
  subscribe(onData: (data: BroadcastMessage) => void, onError?: (error: Event) => void): void {
    if (this.eventSource) {
      console.warn('Already subscribed');
      return;
    }

    const url = `${this.serverUrl}/subscribe?deviceId=${encodeURIComponent(this.deviceId)}`;
    console.log(`📡 Subscribing to: ${url}`);

    this.eventSource = new EventSource(url);

    this.eventSource.onopen = () => {
      console.log('✅ SSE connection opened');
      this.isConnected = true;
      this.reconnectAttempts = 0;
    };

    this.eventSource.onmessage = (event) => {
      try {
        console.log('[SSE] raw data received:', event.data.substring(0, 100));
        const data = JSON.parse(event.data);
        console.log('[SSE] parsed:', data.type || '(no type)', 'deviceId:', data.deviceId);
        
        // 연결 확인 메시지는 건너뛰기
        if (data.type === 'connected') {
          console.log('[SSE] 📡 Connected (skipped)');
          return;
        }
        
        console.log('[SSE] ✅ calling onData with payload keys:', Object.keys(data.payload || {}));
        onData(data as BroadcastMessage);
      } catch (error) {
        console.error('[SSE] Failed to parse SSE data:', error);
      }
    };

    this.eventSource.onerror = (error) => {
      console.error('SSE connection error:', error);
      this.isConnected = false;

      if (onError) {
        onError(error);
      }

      // 자동 재연결 (SSE가 기본적으로 시도하지만, 명시적으로 처리)
      if (this.autoReconnect && this.reconnectAttempts < this.maxReconnectAttempts) {
        this.reconnectAttempts++;
        console.log(`🔄 Reconnecting... (${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
        
        setTimeout(() => {
          if (this.eventSource) {
            this.unsubscribe();
            this.subscribe(onData, onError);
          }
        }, this.reconnectDelay * this.reconnectAttempts);
      }
    };
  }

  /**
   * 구독 해제
   */
  unsubscribe(): void {
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
      this.isConnected = false;
      console.log('🔌 Unsubscribed from broadcast server');
    }
  }

  /**
   * 연결 상태 확인
   */
  getConnectionStatus(): boolean {
    return this.isConnected && this.eventSource !== null;
  }

  /**
   * Health check
   */
  async checkHealth(): Promise<any> {
    try {
      const response = await fetch(`${this.serverUrl}/health`);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Health check failed:', error);
      throw error;
    }
  }

  /**
   * 서버 통계 조회
   */
  async getStats(): Promise<any> {
    try {
      const response = await fetch(`${this.serverUrl}/stats`);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Failed to get stats:', error);
      throw error;
    }
  }

  /**
   * Device 정보 조회
   */
  async getDeviceInfo(deviceId?: string): Promise<any> {
    try {
      const targetDeviceId = deviceId || this.deviceId;
      const response = await fetch(`${this.serverUrl}/device/${targetDeviceId}`);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Failed to get device info:', error);
      throw error;
    }
  }

  /**
   * Device ID 변경
   */
  setDeviceId(deviceId: string): void {
    this.deviceId = deviceId;
    console.log(`📱 Device ID updated to: ${deviceId}`);
  }

  /**
   * 서버 URL 변경
   */
  setServerUrl(serverUrl: string): void {
    this.serverUrl = serverUrl;
    console.log(`🌐 Server URL updated to: ${serverUrl}`);
  }
}

